/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useState, Fragment, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { Input, Label, Modal, Button, Control, ModalContent, ModalFooter, ModalHeader } from '@forge/common';

import Toast from '../../reusablecomponents/toast';

import { useDeleteDeviceMutation } from '../../services/api';

import { LOCALE_CONST } from '../../utils/constants';
import { gatewayErrorStatusMessages } from '../../utils/utility';

import { DeleteModalProps, ToastTypeProps } from '../../models';

import './index.scss';

const DeleteModal: React.FC<DeleteModalProps> = ({ isOpen, onClose, deviceId, deviceName, refetchDeviceList }) => {
	const { t } = useTranslation();

	const convertFn = (value: string) => t(value);

	const CONFIRMATION_TEXT = "Delete";

	const [showToast, setShowToast] = useState<boolean>(false);

	const [inputValue, setInputValue] = useState<string>('');
	const [toastContent, setToastContent] = useState<string>('');
	const [toastType, setToastType] = useState<ToastTypeProps>('success');

	const [deleteDevice] = useDeleteDeviceMutation();

	const displayToast = (type: ToastTypeProps, content: string) => {
		setToastType(type);
		setToastContent(content);

		if (type === "success") {
			refetchDeviceList();
		}

		setShowToast(true);

		onClose();
	};

	const handleDelete = () => {
		deleteDevice({ deviceId })
			.then((response: any) => {
				let content: string = "";
				let type: ToastTypeProps = "success";

				if ('data' in response) {
					content = t(LOCALE_CONST.deleteSuccess, { gatewayName: deviceName });
				} else if ('error' in response) {
					type = "error";
					content = gatewayErrorStatusMessages(convertFn, t(LOCALE_CONST.deleteFailed), response?.error?.status);
				}

				displayToast(type, content);
			}).catch((error: any) => {
				displayToast("error", gatewayErrorStatusMessages(convertFn, t(LOCALE_CONST.deleteFailed), error?.error?.status));
			});
	};

	useEffect(() => {
		if (showToast) {
			const TIME_OUT = 3000;

			const timer = setTimeout(() => {
				setShowToast(false);
			}, TIME_OUT);

			return () => clearTimeout(timer);
		}
	}, [showToast]);

	useEffect(() => {
		if (isOpen) {
			setInputValue('');
		}
	}, [isOpen]);

	const renderConfirmationText = () => <div>{t(LOCALE_CONST.deleteMapConfirm, { gatewayName: deviceName })}</div>;

	const renderDeleteModal = () => {
		return (
			<Modal onModalClose={onClose} onBackdropClick={onClose} className='delete-modal'>
				<ModalHeader title={t(LOCALE_CONST.deleteGateway)} />
				<ModalContent padded>
					<div>
						{renderConfirmationText()}
						<Control>
							<Label className='confirm-text'>{`${t(LOCALE_CONST.deleteConfirmText, { InputText: CONFIRMATION_TEXT })} *`}</Label>
							<Input id="delete-confirm-input" value={inputValue}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
								aria-label="confirm-delete-input" className='confirm-input'
								data-testid="delete-input-test-id"
							/>
						</Control>
					</div>
				</ModalContent>
				<ModalFooter
					rightSlot={
						<div className="delete-modal-footer">
							<Button onClick={onClose} variant="secondary" data-testid="cancel-button-test-id">{t(LOCALE_CONST.cancel)}</Button>
							<Button onClick={handleDelete} variant="primary"
								disabled={inputValue !== CONFIRMATION_TEXT}
								data-testid="delete-button-test-id"
							>
								{t(LOCALE_CONST.delete)}
							</Button>
						</div>
					}
				/>
			</Modal>
		);
	};

	const renderToastComp = () => {
		return (
			<div className="toast-container">
				<Toast id="delete-toast" showClose={false} showDismiss={false}
					title={toastContent} type={toastType}
					lifetime={3000} subtitle={''} content={''} />
			</div>
		);
	};

	return (
		<Fragment>
			{isOpen && renderDeleteModal()}

			{showToast && renderToastComp()}
		</Fragment>
	);
};

export default DeleteModal;